import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import type { Api } from "../../shared/api";
import type { MachineView } from "../../shared/contracts";
import { Button, Card, EmptyState, Field, Input, LinkButton, Loading, Notice, PageHeader, Switch, Textarea } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { usePageTitle } from "../lib/hooks";
import { invalidate } from "../lib/store";

type Pairing = Api["GET /api/machines/pair/:userCode"]["res"];

/** "abcd efgh" / "ABCD-EFGH" → "ABCD-EFGH"; null when it cannot be a pairing code. */
function normalizeCode(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (compact.length < 4 || compact.length > 16) return null;
  return compact.length === 8 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

const policySchema = z.object({
  allowedRoots: z.array(z.string().trim().min(1, "Chemin vide.").max(500, "Chemin trop long.")).min(1, "Indique au moins un dossier.").max(20, "20 dossiers maximum."),
  unattendedGui: z.boolean(),
  shell: z.boolean(),
});

const nameSchema = z.string().trim().max(60, "60 caractères maximum.");

/**
 * /link?code=XXXX-XXXX — approve a computer that runs `pulsez-computer pair`. The code in the URL only selects the
 * request: the user must type the code shown on the computer (anti-phishing), which the server checks again.
 */
export function LinkPage({ code }: { code: string | undefined }) {
  usePageTitle("Relier un ordinateur");
  const urlCode = normalizeCode(code);
  const [lookupCode, setLookupCode] = useState<string | null>(urlCode);
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(urlCode));
  const [done, setDone] = useState<MachineView | "denied" | null>(null);
  const requested = useRef<string | null>(null);

  useEffect(() => {
    if (!lookupCode || requested.current === lookupCode) return;
    requested.current = lookupCode; // one lookup per code: these requests are rate-limited
    setLoading(true);
    setLoadError(null);
    api("GET /api/machines/pair/:userCode", { params: { userCode: lookupCode } })
      .then((p) => setPairing(p))
      .catch((err: unknown) => {
        setPairing(null);
        setLoadError(err instanceof ApiError && err.status === 404 ? "Ce code est inconnu ou a expiré. Relance la commande sur l'ordinateur." : errorText(err));
      })
      .finally(() => setLoading(false));
  }, [lookupCode]);

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader title="Relier un ordinateur" description="Ton agent pourra travailler sur cet ordinateur, dans les limites que tu fixes ici." />
      {done === "denied" ? (
        <EmptyState title="Demande refusée" action={<LinkButton to="/ordinateur">Retour</LinkButton>}>
          Cet ordinateur n'a pas été relié.
        </EmptyState>
      ) : done ? (
        <EmptyState title="Ordinateur relié" action={<LinkButton to="/ordinateur">Voir la liste</LinkButton>}>
          « {done.name} » est maintenant relié. Tu peux fermer le terminal : le programme continue en arrière-plan tant qu'il tourne.
        </EmptyState>
      ) : !lookupCode ? (
        <CodeLookup onCode={(c) => setLookupCode(c)} />
      ) : loading ? (
        <Loading />
      ) : loadError || !pairing ? (
        <div className="space-y-4">
          <Notice tone="danger" role="alert">
            {loadError ?? "Impossible de charger cette demande."}
          </Notice>
          <Button
            variant="secondary"
            onClick={() => {
              requested.current = null;
              setLookupCode(null);
              setLoadError(null);
            }}
          >
            Saisir un autre code
          </Button>
        </div>
      ) : (
        <ApproveForm pairing={pairing} onDone={setDone} />
      )}
    </div>
  );
}

function CodeLookup({ onCode }: { onCode: (code: string) => void }) {
  const id = useId();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <Card className="p-5">
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const c = normalizeCode(value);
          if (!c) {
            setError("Le code ressemble à ABCD-EFGH.");
            return;
          }
          onCode(c);
        }}
      >
        <Field label="Code affiché sur l'ordinateur" htmlFor={id} error={error} hint="Lance « npx @pulsez/computer pair » sur l'ordinateur : il affiche ce code.">
          <Input
            id={id}
            value={value}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={20}
            className="font-mono tracking-widest uppercase"
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
          />
        </Field>
        <Button type="submit">Continuer</Button>
      </form>
    </Card>
  );
}

function ApproveForm({ pairing, onDone }: { pairing: Pairing; onDone: (result: MachineView | "denied") => void }) {
  const ids = { name: useId(), roots: useId(), typed: useId(), gui: useId(), shell: useId() };
  const [name, setName] = useState(pairing.name);
  const [roots, setRoots] = useState("~/Pulsez");
  const [gui, setGui] = useState(false);
  const [shell, setShell] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ roots?: string; typed?: string; name?: string }>({});

  const pending = pairing.status === "pending" && pairing.expiresAt > Date.now();

  async function approve(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const errs: typeof fieldErrors = {};
    const typedCode = normalizeCode(typed);
    if (!typedCode) errs.typed = "Recopie le code affiché sur l'ordinateur.";
    const policy = policySchema.safeParse({
      allowedRoots: roots.split("\n").map((r) => r.trim()).filter(Boolean),
      unattendedGui: gui,
      shell,
    });
    if (!policy.success) errs.roots = policy.error.issues[0]?.message ?? "Dossiers invalides.";
    const parsedName = nameSchema.safeParse(name);
    if (!parsedName.success) errs.name = parsedName.error.issues[0]?.message;
    setFieldErrors(errs);
    if (!typedCode || !policy.success || !parsedName.success) return;
    setBusy("approve");
    setError(null);
    try {
      const machine = await api("POST /api/machines/pair/approve", {
        body: { userCode: pairing.userCode, typedCode, policy: policy.data, ...(parsedName.data ? { name: parsedName.data } : {}) },
      });
      invalidate("machines");
      invalidate("dashboard");
      onDone(machine);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function deny() {
    if (busy) return;
    setBusy("deny");
    setError(null);
    try {
      await api("POST /api/machines/pair/deny", { body: { userCode: pairing.userCode } });
      onDone("denied");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <p className="text-[12px] font-semibold tracking-[0.04em] text-muted uppercase">Demande de liaison</p>
        <p className="mt-2 font-mono text-[26px] font-semibold tracking-[0.2em]">{pairing.userCode}</p>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13.5px]">
          <dt className="text-muted">Ordinateur</dt>
          <dd className="min-w-0 wrap-anywhere">{pairing.name}</dd>
          <dt className="text-muted">Système</dt>
          <dd>{[pairing.os, pairing.arch].filter(Boolean).join(" · ") || "Inconnu"}</dd>
          <dt className="text-muted">Demandé</dt>
          <dd>{fmtDateTime(pairing.requestedAt)}</dd>
          <dt className="text-muted">Adresse IP</dt>
          <dd className="font-mono text-[12.5px] wrap-anywhere">{pairing.requestIp ?? "Inconnue"}</dd>
          <dt className="text-muted">Pays</dt>
          <dd>{pairing.requestCountry ?? "Inconnu"}</dd>
        </dl>
      </Card>

      {!pending ? (
        <Notice tone="warning" role="alert">
          {pairing.status === "approved"
            ? "Cette demande a déjà été acceptée."
            : pairing.status === "denied"
              ? "Cette demande a été refusée."
              : "Cette demande a expiré. Relance la commande sur l'ordinateur pour obtenir un nouveau code."}
        </Notice>
      ) : (
        <Card className="p-5">
          <Notice tone="warning" className="mb-4">
            N'autorise que si tu viens de lancer pulsez-computer sur cet ordinateur et qu'il affiche ce code.
          </Notice>
          <form onSubmit={approve} noValidate className="space-y-4">
            <Field label="Nom de l'ordinateur" htmlFor={ids.name} error={fieldErrors.name}>
              <Input id={ids.name} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Dossiers autorisés" htmlFor={ids.roots} error={fieldErrors.roots} hint="Un chemin par ligne. Ton agent ne pourra lire et écrire que là.">
              <Textarea id={ids.roots} value={roots} className="min-h-20 font-mono text-[13px]" onChange={(e) => setRoots(e.target.value)} />
            </Field>
            <Switch
              id={ids.gui}
              label="Contrôle de l'écran sans moi"
              description="Il peut utiliser la souris et le clavier même quand tu n'es pas devant l'ordinateur."
              checked={gui}
              onChange={setGui}
            />
            <Switch id={ids.shell} label="Autoriser le terminal" description="Il peut lancer des commandes, dans les dossiers autorisés." checked={shell} onChange={setShell} />
            <Field label="Code affiché sur l'ordinateur" htmlFor={ids.typed} error={fieldErrors.typed} hint="Recopie le code que montre le terminal de l'ordinateur.">
              <Input
                id={ids.typed}
                value={typed}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                maxLength={20}
                className="font-mono tracking-widest uppercase"
                onChange={(e) => setTyped(e.target.value)}
              />
            </Field>
            {error ? (
              <Notice tone="danger" role="alert">
                {error}
              </Notice>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" busy={busy === "approve"} disabled={busy !== null}>
                Autoriser cet ordinateur
              </Button>
              <Button variant="secondary" busy={busy === "deny"} disabled={busy !== null} onClick={() => void deny()}>
                Refuser
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
